// BarcodeScanner (design-system §4.27): Kamera mit Sucher – und ein Eingabefeld, sobald die Kamera fehlt oder
// verweigert wird. Ein Vorgang darf nie an der Kamera scheitern.
// Erkennung: BarcodeDetector des Browsers, sonst der WASM-Decoder aus `barcode-detector` (wird nachgeladen;
// die WASM-Datei kommt aus dem eigenen Paket, nicht von einem CDN).
import { Flashlight, FlashlightOff, Keyboard, ScanLine } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { nativeHaptic } from '../lib/native'
import { toScanResult, type BarcodeFormat, type ScanResult } from '../lib/scan'
import { Button } from './Button'
import { LayerProvider, Z } from './context'
import { IconButton } from './IconButton'
import { Input } from './Input'
import { Spinner } from './Spinner'

export type { BarcodeFormat, ScanResult } from '../lib/scan'

export interface ScanFeedback {
  status: 'ok' | 'warning' | 'unknown' | 'error'
  message?: string
}

export type ScannerErrorKind = 'permission-denied' | 'no-camera' | 'unsupported' | 'unknown'

export interface BarcodeScannerProps {
  /** Kamera läuft nur, wenn true und sichtbar */
  active: boolean
  onDetect: (result: ScanResult) => ScanFeedback | Promise<ScanFeedback>
  /** Standard: ean_13, ean_8, code_128, itf, qr_code, data_matrix */
  formats?: BarcodeFormat[]
  /** 'continuous' für Listen-Vorgänge */
  mode?: 'single' | 'continuous'
  /** Standard 1200: derselbe Code zählt nicht doppelt */
  cooldownMs?: number
  /** 'strip': 96 px hoher Streifen über einer Liste */
  shape?: 'viewfinder' | 'strip'
  torch?: boolean
  onTorchChange?: (on: boolean) => void
  /** Schaltfläche „Code tippen“, Standard true */
  manualEntry?: boolean
  /** Standard beides an */
  feedback?: { sound?: boolean; haptic?: boolean }
  onError?: (error: { kind: ScannerErrorKind; message: string }) => void
  className?: string
}

const DEFAULT_FORMATS: BarcodeFormat[] = ['ean_13', 'ean_8', 'code_128', 'itf', 'qr_code', 'data_matrix']

const ERROR_TEXT: Record<ScannerErrorKind, string> = {
  'permission-denied': 'Der Zugriff auf die Kamera wurde nicht erlaubt.',
  'no-camera': 'An diesem Gerät wurde keine Kamera gefunden.',
  unsupported: 'Dieser Browser kann die Kamera hier nicht verwenden.',
  unknown: 'Die Kamera konnte nicht gestartet werden.',
}

interface Detector {
  detect: (source: HTMLVideoElement | HTMLCanvasElement) => Promise<{ rawValue: string; format: string }[]>
}
interface DetectorClass {
  new (options: { formats: string[] }): Detector
  getSupportedFormats?: () => Promise<string[]>
}

/** Pause zwischen zwei Erkennungen (nach dem Ende der vorigen – nie zwei zugleich) */
export const DETECT_GAP_MS = 160
/** Der WASM-Decoder bekommt das Bild höchstens so breit (lange Seite in px) */
export const WASM_MAX_EDGE = 960

async function createDetector(formats: BarcodeFormat[]): Promise<{ detector: Detector; wasm: boolean }> {
  const Native = (globalThis as { BarcodeDetector?: DetectorClass }).BarcodeDetector
  if (Native) {
    try {
      const supported = (await Native.getSupportedFormats?.()) ?? []
      const usable = formats.filter((format) => supported.includes(format))
      if (usable.length > 0) return { detector: new Native({ formats: usable }), wasm: false }
    } catch {
      // weiter mit dem WASM-Decoder
    }
  }
  const [ponyfill, wasm] = await Promise.all([
    import('barcode-detector/ponyfill'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ])
  ponyfill.prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasm.default : prefix + path),
    },
  })
  return { detector: new ponyfill.BarcodeDetector({ formats }) as unknown as Detector, wasm: true }
}

/**
 * Bild für den WASM-Decoder: das Kamerabild, auf höchstens WASM_MAX_EDGE verkleinert. Der Decoder rechnet im
 * Hauptthread – ein volles 1280er-Bild kostete auf schwachen Geräten bis zu 1,7 s je Bild, und die Oberfläche
 * antwortete solange auf nichts (Android-Zurück kam zu spät an, Befund der Geräteprüfung 16.10.).
 */
function frameOf(video: HTMLVideoElement, canvas: { current: HTMLCanvasElement | null }): HTMLVideoElement | HTMLCanvasElement {
  const width = video.videoWidth
  const height = video.videoHeight
  const scale = width > 0 && height > 0 ? Math.min(1, WASM_MAX_EDGE / Math.max(width, height)) : 1
  if (scale >= 1 || typeof document === 'undefined') return video
  const target = (canvas.current ??= document.createElement('canvas'))
  target.width = Math.round(width * scale)
  target.height = Math.round(height * scale)
  const context = target.getContext('2d', { willReadFrequently: true })
  if (!context) return video
  context.drawImage(video, 0, 0, target.width, target.height)
  return target
}

let audio: AudioContext | undefined
function beep(ok: boolean): void {
  try {
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audio ??= new Ctx()
    const oscillator = audio.createOscillator()
    const gain = audio.createGain()
    oscillator.frequency.value = ok ? 1320 : 220
    gain.gain.setValueAtTime(0.08, audio.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + (ok ? 0.09 : 0.25))
    oscillator.connect(gain).connect(audio.destination)
    oscillator.start()
    oscillator.stop(audio.currentTime + (ok ? 0.1 : 0.26))
  } catch {
    // ohne Ton geht es auch – die sichtbare Zeile bleibt
  }
}

/** Barcode per Kamera lesen. Erkannt: Rahmen kurz in success, Ton, Vibration und eine Textzeile (auch für Screenreader). */
export function BarcodeScanner({
  active,
  onDetect,
  formats = DEFAULT_FORMATS,
  mode = 'single',
  cooldownMs = 1200,
  shape = 'viewfinder',
  torch,
  onTorchChange,
  manualEntry = true,
  feedback,
  onError,
  className,
}: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [state, setState] = useState<'idle' | 'starting' | 'running' | 'failed'>('idle')
  const [failure, setFailure] = useState<ScannerErrorKind | null>(null)
  const [last, setLast] = useState<ScanFeedback | null>(null)
  const [manual, setManual] = useState(false)
  const [code, setCode] = useState('')
  const [torchOn, setTorchOn] = useState(false)
  const [torchAvailable, setTorchAvailable] = useState(false)
  const [playing, setPlaying] = useState(false)
  const handlers = useRef({ onDetect, onError, feedback, mode, cooldownMs })
  handlers.current = { onDetect, onError, feedback, mode, cooldownMs }
  const lastHit = useRef<{ code: string; at: number }>({ code: '', at: 0 })
  const busy = useRef(false)
  const paused = useRef(false)
  const formatKey = formats.join(',')
  const lightOn = torch ?? torchOn

  const report = async (result: ScanResult): Promise<void> => {
    if (busy.current) return
    busy.current = true
    try {
      const answer = await handlers.current.onDetect(result)
      const ok = answer.status === 'ok'
      if (handlers.current.feedback?.sound !== false) beep(ok || answer.status === 'warning')
      if (handlers.current.feedback?.haptic !== false) {
        navigator.vibrate?.(ok ? 40 : [60, 40, 60])
        // in der Merxo-App (WebKit kennt kein vibrate): Haptik der Hülle
        nativeHaptic(ok ? 'success' : answer.status === 'warning' ? 'warning' : 'error')
      }
      setLast({
        status: answer.status,
        message:
          answer.message ??
          (ok
            ? `${result.code} erkannt`
            : answer.status === 'unknown'
              ? `${result.code} ist nicht bekannt`
              : `${result.code} konnte nicht verarbeitet werden`),
      })
      if (ok && handlers.current.mode === 'single') paused.current = true
    } catch {
      setLast({ status: 'error', message: `${result.code} konnte nicht verarbeitet werden` })
    } finally {
      busy.current = false
    }
  }

  useEffect(() => {
    if (!active) {
      setState('idle')
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    paused.current = false
    setPlaying(false)
    setFailure(null)
    setState('starting')

    const fail = (kind: ScannerErrorKind): void => {
      if (cancelled) return
      setFailure(kind)
      setState('failed')
      handlers.current.onError?.({ kind, message: ERROR_TEXT[kind] })
    }

    const start = async (): Promise<void> => {
      if (!navigator.mediaDevices?.getUserMedia) return fail('unsupported')
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        })
      } catch (cause) {
        const name = cause instanceof DOMException ? cause.name : ''
        return fail(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'permission-denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'no-camera'
              : 'unknown',
        )
      }
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      streamRef.current = stream
      const track = stream.getVideoTracks()[0]
      setTorchAvailable(Boolean((track?.getCapabilities?.() as { torch?: boolean } | undefined)?.torch))
      const video = videoRef.current
      if (!video) return
      video.srcObject = stream
      try {
        await video.play()
      } catch {
        // Wiedergabe startet, sobald das Element sichtbar ist
      }
      let created: { detector: Detector; wasm: boolean }
      try {
        created = await createDetector(formatKey.split(',') as BarcodeFormat[])
      } catch {
        return fail('unsupported')
      }
      if (cancelled) return
      setState('running')
      const { detector, wasm } = created
      const canvas: { current: HTMLCanvasElement | null } = { current: null }
      // Eine Erkennung nach der anderen: die nächste erst DETECT_GAP_MS nach dem Ende der vorigen. Mit setInterval
      // stauten sich Aufrufe, sobald eine Erkennung länger dauerte als der Takt, und der Hauptthread kam nicht mehr frei.
      const tick = async (): Promise<void> => {
        if (cancelled) return
        if (!paused.current && !busy.current && !document.hidden && video.readyState >= 2) {
          try {
            const found = await detector.detect(wasm ? frameOf(video, canvas) : video)
            const hit = found[0]
            if (hit && !cancelled) {
              const now = Date.now()
              // derselbe Code zählt innerhalb der Sperrzeit nicht doppelt
              if (!(hit.rawValue === lastHit.current.code && now - lastHit.current.at < handlers.current.cooldownMs)) {
                lastHit.current = { code: hit.rawValue, at: now }
                void report(
                  toScanResult(
                    hit.rawValue,
                    'camera',
                    (formatKey.split(',').includes(hit.format) ? hit.format : 'unknown') as ScanResult['format'],
                  ),
                )
              }
            }
          } catch {
            // ein unlesbares Bild – weiter mit dem nächsten
          }
        }
        if (!cancelled) timer = setTimeout(() => void tick(), DETECT_GAP_MS)
      }
      timer = setTimeout(() => void tick(), DETECT_GAP_MS)
    }
    void start()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [active, formatKey])

  // Taschenlampe
  useEffect(() => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track || !torchAvailable) return
    void track.applyConstraints({ advanced: [{ torch: lightOn } as MediaTrackConstraintSet] }).catch(() => undefined)
  }, [lightOn, torchAvailable, state])

  // Rückmeldung nach kurzer Zeit zurücknehmen (der Rahmen blitzt nur kurz)
  const [flash, setFlash] = useState(false)
  useEffect(() => {
    if (!last) return
    setFlash(true)
    const timer = setTimeout(() => setFlash(false), 700)
    return () => clearTimeout(timer)
  }, [last])

  const submitManual = (): void => {
    const value = code.trim()
    if (value === '') return
    setCode('')
    void report(toScanResult(value, 'manual'))
  }

  const strip = shape === 'strip'
  const showManual = manual || state === 'failed'
  const tone =
    last?.status === 'ok'
      ? 'border-success'
      : last?.status === 'warning' || last?.status === 'unknown'
        ? 'border-warning'
        : 'border-danger'

  return (
    <LayerProvider z={Z.scanner}>
      <div className={cn('flex flex-col gap-2', className)} data-ui="barcode-scanner">
        {state !== 'failed' && (
          <div
            className={cn(
              'relative overflow-hidden border-2 bg-black transition-colors duration-(--duration-fast)',
              strip ? 'h-24 rounded-lg' : 'aspect-4/3 rounded-xl',
              flash ? tone : 'border-transparent',
            )}
          >
            {/* bis das erste Bild da ist unsichtbar: Android-WebViews zeigen sonst ihr graues Wiedergabe-Symbol als Poster */}
            <video
              ref={videoRef}
              muted
              playsInline
              onPlaying={() => setPlaying(true)}
              className={cn('size-full object-cover', !playing && state !== 'running' && 'opacity-0')}
              aria-label="Kamerabild"
              data-playing={playing || state === 'running' ? '' : undefined}
            />
            {/* ruhige Ziellinie, kein Dauer-Animieren */}
            {state === 'running' && (
              <span
                className="pointer-events-none absolute inset-x-6 top-1/2 h-px -translate-y-1/2 bg-white/70"
                aria-hidden
              />
            )}
            {state === 'running' && !strip && (
              <span className="pointer-events-none absolute inset-6 rounded-lg border border-white/40" aria-hidden />
            )}
            {state === 'starting' && (
              <span className="absolute inset-0 flex items-center justify-center gap-2 text-dense text-white">
                <Spinner /> Kamera wird gestartet …
              </span>
            )}
            {state === 'idle' && (
              <span className="absolute inset-0 flex items-center justify-center gap-2 text-dense text-white/80">
                <ScanLine className="size-5" strokeWidth={1.75} aria-hidden /> Kamera ist aus
              </span>
            )}
            <div className="absolute right-2 bottom-2 flex gap-1" data-theme="dark">
              {torchAvailable && (
                <IconButton
                  icon={lightOn ? FlashlightOff : Flashlight}
                  label={lightOn ? 'Licht ausschalten' : 'Licht einschalten'}
                  variant="secondary"
                  pressed={lightOn}
                  onClick={() => {
                    setTorchOn(!lightOn)
                    onTorchChange?.(!lightOn)
                  }}
                />
              )}
              {manualEntry && (
                <IconButton
                  icon={Keyboard}
                  label="Code tippen"
                  variant="secondary"
                  pressed={manual}
                  onClick={() => setManual(!manual)}
                />
              )}
            </div>
          </div>
        )}
        {state === 'failed' && failure && (
          <p className="rounded-md border border-line bg-sunken px-3 py-2 text-dense text-fg-muted" role="status">
            {ERROR_TEXT[failure]} Tippen Sie den Code ein oder verwenden Sie einen Handscanner.
          </p>
        )}
        {showManual && manualEntry && (
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              submitManual()
            }}
          >
            <Input
              label="Code tippen oder Handscanner verwenden"
              labelHidden={state !== 'failed'}
              value={code}
              onChange={setCode}
              mono
              inputMode="numeric"
              placeholder="4105250031104"
              autoFocus={manual}
              className="flex-1"
              onScan={(scanned) => void report(toScanResult(scanned, 'wedge'))}
            />
            <Button type="submit" disabled={code.trim() === ''}>
              Übernehmen
            </Button>
          </form>
        )}
        <p
          aria-live="polite"
          role="status"
          className={cn(
            'min-h-5 text-dense',
            last?.status === 'ok'
              ? 'text-success-fg'
              : last?.status === 'error'
                ? 'text-danger-fg'
                : last
                  ? 'text-warning-fg'
                  : 'text-fg-subtle',
          )}
        >
          {last?.message ?? (state === 'running' ? 'Barcode in die Mitte halten' : '')}
        </p>
      </div>
    </LayerProvider>
  )
}
